import { AddIcon, Checkbox, Grid, GridColumn, GridRow } from '@bp/ui-components';
import { graphql } from 'client/gql';
import { AbsenceReasonModal } from 'components/Absences/modals/AbsenceReasonModal';
import { AbsenceReasonTable } from 'components/Absences/tables/AbsenceReasonTable';
import { BpCard } from 'components/BpCard/BpCard';
import { OrganizationConfigContext } from 'context/OrganizationConfigContext';
import { useConfirm } from 'hooks/useConfirm';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { type FC, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'urql';
import { showErrorToast } from 'utils/showErrorToast';
import { showSuccessToast } from 'utils/showSuccessToast';
import { showSuccessDeleteToast, showUserDbErrorToast } from 'utils/toasts';
import { BpSubpage } from '../../../components/BpSubpage/BpSubpage';
import { useAuthClaims } from '../../../hooks/useAuthClaims';

const DeleteAbsenceReasonMutationDocument = graphql(`
  mutation DeleteAbsenceReason($where: AbsenceReasonWhere!) {
    deleteAbsenceReasons(where: $where) {
      nodesDeleted
    }
  }
`);
const UpdateBildungsplattformsMutationDocument = graphql(`
  mutation UpdateBildungsplattforms($update: BildungsplattformUpdateInput!, $where: BildungsplattformWhere!) {
    updateBildungsplattforms(update: $update, where: $where) {
      bildungsplattforms {
        uuid
        __typename
        allowParentExcuses
      }
    }
  }
`);

const SettingsAbsencesSubpageQuery = graphql(`
  query SettingsAbsencesSubpageData($organizationUuid: ID!) {
    ...AbsenceReasonTableFields
  }
`);

export type AbsenceReason = {
  uuid: string;
  reason: string;
  onlyForRoles: string[];
  internal: boolean;
};

export const SettingsAbsencesSubpage: FC = () => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const organizationUuid = pimAuthClaims.getOrganizationUuid();

  const [{ data }] = useQuery({
    query: SettingsAbsencesSubpageQuery,
    variables: {
      organizationUuid: organizationUuid,
    },
  });

  const permissions = usePermissionChecker();

  const { organizationProducts, allowParentExcuses, setAllowParentExcuses } = useContext(OrganizationConfigContext);

  const [selectedReasonUuid, setSelectedReasonUuid] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);

  const [, deleteAbsence] = useMutation(DeleteAbsenceReasonMutationDocument);
  const [, updateBildungsplattform] = useMutation(UpdateBildungsplattformsMutationDocument);

  const bildungsplattform = organizationProducts?.find((p) => p.__typename === 'Bildungsplattform');

  const { ConfirmationDialog, confirm: confirmDelete } = useConfirm({
    defaultTitle: t('delete.headline'),
    defaultMessage: t('delete.message', {
      type: t('absences.reasons.title', { count: 1 }),
      context: 'female',
    }),
    defaultConfirmText: t('delete.delete'),
  });

  async function handleDelete(uuid: string) {
    setLoading(true);
    const { error } = await deleteAbsence({ where: { uuid } });
    if (error) {
      showUserDbErrorToast();
    } else {
      showSuccessDeleteToast();
    }
    setSelectedReasonUuid(null);
    setLoading(false);
  }

  function handleAdd() {
    setModalOpen(true);
    setSelectedReasonUuid(null);
  }

  function handleEdit(uuid: string) {
    setSelectedReasonUuid(uuid);
    setModalOpen(true);
  }

  function handleClose() {
    setModalOpen(false);
    setSelectedReasonUuid(null);
  }

  const handleAllowParentExcuses = async (value: boolean) => {
    if (bildungsplattform) {
      const resp = await updateBildungsplattform({
        update: {
          allowParentExcuses: value,
        },
        where: { uuid: bildungsplattform.uuid },
      });
      if (resp.error) {
        showErrorToast(resp.error);
      } else {
        setAllowParentExcuses(value);
        showSuccessToast(t('common.saved'));
      }
    }
  };

  return (
    <BpSubpage isForbidden={!permissions?.isOrganizationAdmin({ uuid: organizationUuid })}>
      <Grid>
        <GridRow>
          <GridColumn width={9}>
            <BpCard
              header={{
                headline: t('absences.reasons.title', { count: 2 }),
                actions: [
                  {
                    text: t('common.add'),
                    icon: <AddIcon />,
                    callback: () => handleAdd(),
                  },
                ],
              }}
              noPadding
            >
              <AbsenceReasonTable
                data={data}
                isLoading={loading}
                onEdit={handleEdit}
                onDelete={async (uuid) => {
                  const res = await confirmDelete();
                  if (res) handleDelete(uuid);
                }}
              />
            </BpCard>
          </GridColumn>
          <GridColumn width={3}>
            <BpCard header={{ headline: t('settings.title') }}>
              <Checkbox
                name='allow-parent-excuses'
                checked={allowParentExcuses}
                onChange={async () => {
                  await handleAllowParentExcuses(!allowParentExcuses);
                }}
                label={t('absences.allowParentExcuses')}
              />
            </BpCard>
          </GridColumn>
        </GridRow>
      </Grid>

      <AbsenceReasonModal isOpen={modalOpen} selectedReasonUuid={selectedReasonUuid} onClose={() => handleClose()} />
      <ConfirmationDialog />
    </BpSubpage>
  );
};
